// MARK://dhk8/pos/lib/print/templates
// HTML templates for the common POS print kinds. Designed to look good in THREE
// surfaces from the same source:
//   1. Digital preview dialog in the POS (FastBarPage / OrderPage "Venta cerrada")
//   2. Thermal printer (58 / 80mm) via Bridge or browser print — monospaced + narrow
//   3. PDF download from tickets.nintaigo.com (public-tickets renders its own
//      branded variant; we keep the receipt HTML self-contained as a fallback)
//
// Async because the QR code (URL → tickets.nintaigo.com/{publicTicketUuid}) is
// rendered to a data:image/svg+xml URI via `qrcode`. Callers should await and
// stash the result in component state, then drop into dangerouslySetInnerHTML.
// Kept as plain TS (no React) so non-component code (print queue worker, the
// hooks/usePrinting maybeBrowserPrint path) can call it directly too.

import QRCode from 'qrcode'
import type { OrderDto, OrderItem } from '../../stores/order-store.js'

const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`

export interface TenantHeaderOpts {
  /** Comercial brand shown above the address (e.g. "SAVI's Coffee GDL"). */
  businessName?: string
  /** Branch label (e.g. "Av. México 2305A"). Optional second line. */
  branchName?: string
  /** Full street address, one or several lines. Newlines render as <br>. */
  businessAddress?: string
  /** SAT RFC (e.g. "CAMA930627235") — printed in the fiscal footer. */
  rfc?: string
  /** Legal name of the receptor (e.g. "ABRAHAM CARACHURE MEJIA"). */
  legalName?: string
  /** Régimen fiscal SAT label (e.g. "612 - Personas Físicas..."). Optional. */
  regimeFiscal?: string
  /** Absolute URL to the merchant logo (color version is fine — we monochrome it
   *  for thermal print via CSS). If omitted, falls back to text-only. */
  logoUrl?: string
}

export interface RenderOpts extends TenantHeaderOpts {
  paperKind?: 'thermal-58mm' | 'thermal-80mm'
  /** Override base URL for the QR code. Default `https://tickets.nintaigo.com`. */
  ticketsBaseUrl?: string
}

const DEFAULT_TICKETS_BASE = 'https://tickets.nintaigo.com'

// ── HTML helpers ────────────────────────────────────────────────────────────

function esc(s: string): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string)
}

function escMultiline(s: string): string {
  return esc(s).replace(/\n/g, '<br />')
}

/** Inline CSS for the receipt — works for digital preview AND thermal print.
 *  Uses dashed monochrome borders that look intentional in both color (digital)
 *  and grayscale (thermal). Monospace base ensures alignment doesn't drift on
 *  the printer; the header has its own sans-serif for the brand name. */
const RECEIPT_CSS = `
  .dhk8-rcpt { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size: 12px; line-height: 1.45; color: #111; max-width: 320px; margin: 0 auto; padding: 4px; background: #fff; }
  .dhk8-rcpt__brand { text-align: center; padding: 6px 0 8px; }
  .dhk8-rcpt__logo { max-height: 56px; max-width: 180px; object-fit: contain; margin-bottom: 4px; display: inline-block; }
  .dhk8-rcpt__name { font-family: -apple-system, "Segoe UI", Roboto, sans-serif; font-size: 16px; font-weight: 700; letter-spacing: 0.2px; }
  .dhk8-rcpt__addr { font-size: 11px; color: #444; margin-top: 2px; white-space: pre-line; }
  .dhk8-rcpt__fiscal { font-size: 10px; color: #555; margin-top: 4px; }
  .dhk8-rcpt__divider { border: none; border-top: 1px dashed #555; margin: 6px 0; }
  .dhk8-rcpt__banner { text-align: center; font-weight: 700; font-size: 13px; letter-spacing: 1px; margin: 4px 0; }
  .dhk8-rcpt__meta { display: grid; grid-template-columns: auto 1fr; gap: 2px 8px; font-size: 11px; margin: 4px 0; }
  .dhk8-rcpt__meta .lbl { color: #555; }
  .dhk8-rcpt__meta .val { text-align: right; font-variant-numeric: tabular-nums; }
  .dhk8-rcpt__items { margin-top: 4px; }
  .dhk8-rcpt__line { display: flex; justify-content: space-between; gap: 8px; padding: 2px 0; }
  .dhk8-rcpt__line-name { flex: 1; }
  .dhk8-rcpt__line-amt { white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 600; }
  .dhk8-rcpt__mods { padding-left: 14px; font-size: 10px; color: #555; }
  .dhk8-rcpt__notes { padding-left: 14px; font-size: 10px; font-style: italic; color: #666; }
  .dhk8-rcpt__totals { margin-top: 6px; }
  .dhk8-rcpt__tot-row { display: flex; justify-content: space-between; padding: 2px 0; font-variant-numeric: tabular-nums; }
  .dhk8-rcpt__tot-row.grand { font-weight: 700; font-size: 14px; border-top: 1px solid #111; margin-top: 4px; padding-top: 4px; }
  .dhk8-rcpt__qr-block { text-align: center; margin: 10px 0 6px; }
  .dhk8-rcpt__qr { width: 120px; height: 120px; image-rendering: pixelated; }
  .dhk8-rcpt__qr-url { font-size: 10px; color: #555; margin-top: 2px; word-break: break-all; }
  .dhk8-rcpt__qr-hint { font-size: 10px; color: #777; margin-top: 1px; }
  .dhk8-rcpt__footer { text-align: center; font-size: 10px; color: #666; margin-top: 6px; }
  .dhk8-rcpt__thanks { text-align: center; font-size: 12px; margin-top: 4px; font-weight: 500; }
  @media print {
    .dhk8-rcpt { max-width: 100%; padding: 0; }
    .dhk8-rcpt__logo { filter: grayscale(1) contrast(1.2); }
    .dhk8-rcpt__divider { border-top: 1px dashed #000; }
  }
`

function headerBlock(opts: TenantHeaderOpts): string {
  const out: string[] = []
  out.push('<div class="dhk8-rcpt__brand">')
  if (opts.logoUrl) {
    out.push(`<img class="dhk8-rcpt__logo" src="${esc(opts.logoUrl)}" alt="${esc(opts.businessName ?? 'Logo')}" />`)
  }
  if (opts.businessName) {
    out.push(`<div class="dhk8-rcpt__name">${esc(opts.businessName)}</div>`)
  }
  const addressLines: string[] = []
  if (opts.branchName) addressLines.push(opts.branchName)
  if (opts.businessAddress) addressLines.push(opts.businessAddress)
  if (addressLines.length > 0) {
    out.push(`<div class="dhk8-rcpt__addr">${escMultiline(addressLines.join('\n'))}</div>`)
  }
  if (opts.legalName || opts.rfc) {
    const fiscalLine = [opts.legalName, opts.rfc ? `RFC ${opts.rfc}` : null].filter(Boolean).join(' · ')
    out.push(`<div class="dhk8-rcpt__fiscal">${esc(fiscalLine)}</div>`)
  }
  if (opts.regimeFiscal) {
    out.push(`<div class="dhk8-rcpt__fiscal">Régimen: ${esc(opts.regimeFiscal)}</div>`)
  }
  out.push('</div>')
  return out.join('')
}

function metaBlock(order: OrderDto, when: Date, banner: string): string {
  const rows: string[] = []
  rows.push(`<div class="dhk8-rcpt__banner">${esc(banner)}</div>`)
  rows.push('<div class="dhk8-rcpt__meta">')
  if (order.folio) rows.push(`<span class="lbl">Folio</span><span class="val">${esc(order.folio)}</span>`)
  rows.push(`<span class="lbl">Fecha</span><span class="val">${esc(when.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }))}</span>`)
  if (typeof order.covers === 'number' && order.covers > 0) {
    rows.push(`<span class="lbl">Comensales</span><span class="val">${order.covers}</span>`)
  }
  rows.push('</div>')
  return rows.join('')
}

function lineItem(it: OrderItem): string {
  // El importe de línea incluye los modificadores con costo (extras): unitPriceCents
  // es solo el base del producto; cada modifier.priceDeltaCents (por unidad) se suma
  // encima. Omitirlos subvaluaba el ticket digital — bug SAVIs ORD-000197 (mostraba
  // $191 en vez de $211). Mismo criterio que el ticket impreso y el ticket público (QR).
  const modSum = (it.modifiers ?? []).reduce((s, m) => s + (m.priceDeltaCents ?? 0), 0)
  const lineTotal = ((it.unitPriceCents ?? 0) + modSum) * it.quantity
  const out: string[] = []
  out.push('<div class="dhk8-rcpt__line">')
  out.push(`<span class="dhk8-rcpt__line-name">${it.quantity} × ${esc(it.name)}</span>`)
  out.push(`<span class="dhk8-rcpt__line-amt">${fmtMoney(lineTotal)}</span>`)
  out.push('</div>')
  if (it.modifiers && it.modifiers.length > 0) {
    const mods = it.modifiers.map((m) => `+ ${esc(m.name)}${m.priceDeltaCents ? ' ' + fmtMoney(m.priceDeltaCents) : ''}`).join('<br />')
    out.push(`<div class="dhk8-rcpt__mods">${mods}</div>`)
  }
  if (it.notes) {
    out.push(`<div class="dhk8-rcpt__notes">Nota: ${esc(it.notes)}</div>`)
  }
  return out.join('')
}

function totalsBlock(order: OrderDto): string {
  // Fuente de verdad: los totales YA calculados y guardados en la orden — idénticos
  // al ticket impreso (toReceipt/buildReceipt), al ticket público (QR) y al cobro
  // (PosPayment). Antes este bloque recomputaba el subtotal desde unitPriceCents SIN
  // los modificadores, subvaluando el total (bug SAVIs ORD-000197: $191 vs $211 real).
  // Solo si la orden no trae totales (preview de un borrador sin cerrar) caemos a
  // recomputar desde las líneas, ahora incluyendo los extras.
  const items = order.items ?? []
  const modSum = (it: OrderItem) => (it.modifiers ?? []).reduce((s, m) => s + (m.priceDeltaCents ?? 0), 0)
  const grossFromItems = items.reduce((s, it) => s + ((it.unitPriceCents ?? 0) + modSum(it)) * it.quantity, 0)
  const tip = order.tipCents ?? 0
  const discount = order.discountCents ?? 0
  const hasStoredTotals = (order.totalCents ?? 0) > 0
  const subtotal = hasStoredTotals ? order.subtotalCents : Math.round(grossFromItems / 1.16)
  const iva = hasStoredTotals ? order.taxCents : grossFromItems - Math.round(grossFromItems / 1.16)
  const total = hasStoredTotals ? order.totalCents : grossFromItems + tip - discount
  return `<div class="dhk8-rcpt__totals">
  <div class="dhk8-rcpt__tot-row"><span>Subtotal</span><span>${fmtMoney(subtotal)}</span></div>
  ${discount > 0 ? `<div class="dhk8-rcpt__tot-row"><span>Descuento</span><span>-${fmtMoney(discount)}</span></div>` : ''}
  <div class="dhk8-rcpt__tot-row"><span>IVA 16%</span><span>${fmtMoney(iva)}</span></div>
  ${tip > 0 ? `<div class="dhk8-rcpt__tot-row"><span>Propina</span><span>${fmtMoney(tip)}</span></div>` : ''}
  <div class="dhk8-rcpt__tot-row grand"><span>TOTAL</span><span>${fmtMoney(total)}</span></div>
</div>`
}

async function qrBlock(publicTicketUuid: string | null | undefined, base: string): Promise<string> {
  if (!publicTicketUuid) return ''
  const url = `${base}/${publicTicketUuid}`
  let qrSvg = ''
  try {
    qrSvg = await QRCode.toString(url, { type: 'svg', errorCorrectionLevel: 'M', margin: 1, width: 120 })
  } catch {
    // QR generation failure shouldn't kill the receipt — drop the block.
    return ''
  }
  // Embed the SVG inline. Strip the XML declaration to keep it cleanly inline.
  const cleanSvg = qrSvg.replace(/<\?xml[^?]*\?>/, '').trim()
  return `<div class="dhk8-rcpt__qr-block">
  <div class="dhk8-rcpt__qr">${cleanSvg}</div>
  <div class="dhk8-rcpt__qr-url">${esc(url.replace(/^https?:\/\//, ''))}</div>
  <div class="dhk8-rcpt__qr-hint">Consulta y descarga este ticket en línea</div>
</div>`
}

function wrap(body: string): string {
  return `<style>${RECEIPT_CSS}</style><div class="dhk8-rcpt">${body}</div>`
}

// ── Public renderers (async — caller awaits) ─────────────────────────────────

export async function renderReceipt(order: OrderDto, opts: RenderOpts = {}): Promise<string> {
  const when = order.closedAt ? new Date(order.closedAt) : new Date()
  const base = opts.ticketsBaseUrl ?? DEFAULT_TICKETS_BASE
  const body = [
    headerBlock(opts),
    `<hr class="dhk8-rcpt__divider" />`,
    metaBlock(order, when, 'TICKET'),
    `<hr class="dhk8-rcpt__divider" />`,
    `<div class="dhk8-rcpt__items">${(order.items ?? []).map(lineItem).join('')}</div>`,
    `<hr class="dhk8-rcpt__divider" />`,
    totalsBlock(order),
    await qrBlock(order.publicTicketUuid, base),
    `<div class="dhk8-rcpt__thanks">¡Gracias por tu visita!</div>`,
  ].join('')
  return wrap(body)
}

export async function renderPrecuenta(order: OrderDto, opts: RenderOpts = {}): Promise<string> {
  const when = new Date()
  const body = [
    headerBlock(opts),
    `<hr class="dhk8-rcpt__divider" />`,
    metaBlock(order, when, 'PRECUENTA — NO ES TICKET'),
    `<hr class="dhk8-rcpt__divider" />`,
    `<div class="dhk8-rcpt__items">${(order.items ?? []).map(lineItem).join('')}</div>`,
    `<hr class="dhk8-rcpt__divider" />`,
    totalsBlock(order),
    `<div class="dhk8-rcpt__footer">Pase a caja para cobrar.</div>`,
  ].join('')
  return wrap(body)
}

export function renderKitchen(order: OrderDto, opts: RenderOpts & { stationName?: string; items?: OrderItem[]; tableLabel?: string; serverName?: string } = {}): string {
  // Kitchen ticket: minimal monospace — no logo, no QR, no totals. Items + notes only.
  const items = opts.items ?? order.items ?? []
  const when = new Date()
  const station = opts.stationName ?? 'COCINA'
  const where = opts.tableLabel ?? (order.type === 'TAKEOUT' ? 'TAKEOUT' : 'EN SALA')
  const body = [
    `<div style="text-align:center;font-weight:bold;font-size:14px">${esc(station)}</div>`,
    `<div style="text-align:center">${esc(where)}${opts.serverName ? ` · ${esc(opts.serverName)}` : ''}</div>`,
    `<hr class="dhk8-rcpt__divider" />`,
    items.map((it) => `
<div style="font-size:13px;font-weight:bold">${it.quantity} × ${esc(it.name)}</div>
${it.modifiers && it.modifiers.length ? `<div class="dhk8-rcpt__mods" style="font-size:11px">${it.modifiers.map((m) => `+ ${esc(m.name)}`).join('<br />')}</div>` : ''}
${it.notes ? `<div class="dhk8-rcpt__notes" style="font-size:11px">Nota: ${esc(it.notes)}</div>` : ''}`).join(''),
    `<hr class="dhk8-rcpt__divider" />`,
    `<div style="text-align:center;font-size:10px">${esc(when.toLocaleTimeString('es-MX'))}${order.folio ? ` · Folio ${esc(order.folio)}` : ''}</div>`,
  ].join('')
  return wrap(body)
}
